import { apiClient } from "./apiService";

class MatchService {
  // Legacy no-op — auth is handled via httpOnly cookie through apiClient
  setAuthToken(_token) {}

  /**
   * Create a new match
   */
  async createMatch(matchData) {
    try {
      const response = await apiClient.post("/matches", matchData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get match by ID
   */
  async getMatchById(matchId) {
    try {
      const response = await apiClient.get(`/matches/${matchId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tournament matches
   */
  async getTournamentMatches(tournamentId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/matches/tournament/${tournamentId}${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Start a match
   */
  async startMatch(matchId) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/start`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Re-send a pending challenge notification to the opponent
   */
  async resendChallenge(matchId) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/resend-challenge`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Complete a match
   */
  async completeMatch(matchId, completionData = {}) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/complete`, completionData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add scoring event to match
   */
  async addScoringEvent(matchId, eventData) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/events`, eventData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get live match data
   */
  async getLiveMatchData(matchId) {
    try {
      const response = await apiClient.get(`/matches/${matchId}/live`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get match events
   */
  async getMatchEvents(matchId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/matches/${matchId}/events${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get participant match history
   */
  async getParticipantMatches(participantId, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/matches/participant/${participantId}${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cancel a match
   */
  async cancelMatch(matchId, reason) {
    try {
      // The backend stores the reason and tells both sides (MT-B16)
      const response = await apiClient.post(
        `/matches/${matchId}/cancel`,
        reason ? { reason } : {}
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * The challenged side withdraws from an accepted challenge (before it starts)
   */
  async withdrawMatch(matchId) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/withdraw`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update match details
   */
  async updateMatch(matchId, updateData) {
    try {
      const response = await apiClient.put(`/matches/${matchId}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete match
   */
  async deleteMatch(matchId) {
    try {
      const response = await apiClient.delete(`/matches/${matchId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Initiate a score correction for a completed match (host only, within 2h).
   */
  async initiateScoreCorrection(matchId, { proposedScore, correctionReason }) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/score-correction`, {
        proposedScore,
        correctionReason,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Vote on a pending score correction.
   * @param {string} vote - "accept" | "reject"
   */
  async voteScoreCorrection(matchId, vote) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/score-correction/vote`, { vote });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Permanently lock the match score (host only). No corrections possible after this.
   */
  async finalizeMatch(matchId) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/finalize`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add score to match
   */
  async addScore(matchId, scoreData) {
    try {
      const response = await apiClient.post(`/matches/${matchId}/score`, scoreData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all matches with pagination
   */
  async getAllMatches(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `/matches${queryString ? `?${queryString}` : ""}`;
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get scoring template for sport
   */
  async getScoringTemplate(sportId) {
    try {
      const response = await apiClient.get(`/matches/sport/${sportId}/template`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk create matches for tournament
   */
  async bulkCreateMatches(tournamentId, matches) {
    try {
      const response = await apiClient.post(`/matches/tournament/${tournamentId}/bulk`, { matches });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        message: data.message || "An error occurred",
        status,
        details: data.details || null,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: "Network error - please check your connection",
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || "An unexpected error occurred",
        status: -1,
      };
    }
  }

  /**
   * Real-time match data polling
   */
  startLiveMatchPolling(matchId, callback, interval = 5000) {
    const poll = async () => {
      try {
        const liveData = await this.getLiveMatchData(matchId);
        callback(null, liveData.data);
      } catch (error) {
        callback(error, null);
      }
    };

    // Initial call
    poll();

    // Set up interval
    const intervalId = setInterval(poll, interval);

    // Return function to stop polling
    return () => clearInterval(intervalId);
  }

  /**
   * Format score display based on sport type
   */
  formatScoreDisplay(scoreData, sportType) {
    if (!scoreData) return "0 - 0";

    switch (sportType) {
      case "goals":
      case "points":
        return `${scoreData.participant1?.total || 0} - ${scoreData.participant2?.total || 0}`;

      case "sets_games":
        const p1Sets = scoreData.participant1?.setsWon || 0;
        const p2Sets = scoreData.participant2?.setsWon || 0;
        const p1Games = scoreData.participant1?.currentSet?.games || 0;
        const p2Games = scoreData.participant2?.currentSet?.games || 0;
        return `${p1Sets} - ${p2Sets} (${p1Games} - ${p2Games})`;

      case "runs_wickets":
        const p1Runs = scoreData.participant1?.runs || 0;
        const p1Wickets = scoreData.participant1?.wickets || 0;
        const p2Runs = scoreData.participant2?.runs || 0;
        const p2Wickets = scoreData.participant2?.wickets || 0;
        return `${p1Runs}/${p1Wickets} - ${p2Runs}/${p2Wickets}`;

      default:
        return `${scoreData.participant1?.total || 0} - ${scoreData.participant2?.total || 0}`;
    }
  }

  /**
   * Get event type options based on sport
   */
  getEventTypesForSport(sportType) {
    const eventTypes = {
      goals: [
        { value: "goal", label: "Goal", icon: "⚽" },
        { value: "yellow_card", label: "Yellow Card", icon: "🟨" },
        { value: "red_card", label: "Red Card", icon: "🟥" },
        { value: "substitution", label: "Substitution", icon: "🔄" },
      ],
      points: [
        { value: "field_goal", label: "Field Goal", icon: "🏀" },
        { value: "three_pointer", label: "3-Pointer", icon: "🎯" },
        { value: "free_throw", label: "Free Throw", icon: "🎯" },
        { value: "foul", label: "Foul", icon: "⚠️" },
        { value: "timeout", label: "Timeout", icon: "⏱️" },
      ],
      sets_games: [
        { value: "point_won", label: "Point Won", icon: "🎾" },
        { value: "ace", label: "Ace", icon: "🎯" },
        { value: "double_fault", label: "Double Fault", icon: "❌" },
        { value: "winner", label: "Winner", icon: "✨" },
        { value: "error", label: "Unforced Error", icon: "💥" },
      ],
      runs_wickets: [
        { value: "runs", label: "Runs", icon: "🏏" },
        { value: "wicket", label: "Wicket", icon: "🎯" },
        { value: "boundary", label: "Boundary (4)", icon: "🏃‍♂️" },
        { value: "six", label: "Six", icon: "🚀" },
        { value: "over_complete", label: "Over Complete", icon: "✅" },
      ],
    };

    return (
      eventTypes[sportType] || [
        { value: "score", label: "Score", icon: "🎯" },
        { value: "foul", label: "Foul", icon: "⚠️" },
      ]
    );
  }

  /**
   * Validate event data before submission
   */
  validateEventData(eventData, sportType) {
    const errors = [];

    if (!eventData.participant_id) {
      errors.push("Participant is required");
    }

    if (!eventData.event_type) {
      errors.push("Event type is required");
    }

    if (typeof eventData.event_time !== "number" || eventData.event_time < 0) {
      errors.push("Valid event time is required");
    }

    // Sport-specific validations
    if (sportType === "points" && eventData.event_type === "field_goal") {
      if (
        !eventData.event_data ||
        typeof eventData.event_data.isThreePointer === "undefined"
      ) {
        errors.push("Field goal type (2pt or 3pt) is required");
      }
    }

    if (sportType === "runs_wickets" && eventData.event_type === "runs") {
      if (!eventData.event_data || !eventData.event_data.runs) {
        errors.push("Number of runs is required");
      }
    }

    return errors;
  }

  /**
   * Check whether participant(s) are free to join a new match.
   * Calls GET /matches/validate-availability.
   *
   * @param {Object} params
   * @param {number|string} params.participant1Id
   * @param {string}        [params.participant1Type='user']
   * @param {number|string} params.participant2Id
   * @param {string}        [params.participant2Type='user']
   * @returns {Promise<{ available: boolean, busyTeams?, busyPlayers?, message? }>}
   */
  async validateParticipantAvailability({
    participant1Id,
    participant1Type = 'user',
    participant2Id,
    participant2Type = 'user',
  }) {
    try {
      const params = new URLSearchParams({
        participant_1_id: String(participant1Id),
        participant_1_type: participant1Type,
        participant_2_id: String(participant2Id),
        participant_2_type: participant2Type,
      });
      const response = await apiClient.get(`/matches/validate-availability?${params.toString()}`);
      return response.data?.data ?? { available: true };
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

// Create singleton instance
const matchService = new MatchService();
export default matchService;
